/* useCmsPageRoutes — fetches the current slug map for the 5 CMS pages.
 *
 * Returns a map: { <key>: { path, default } }
 *
 * While the backend call is in flight, default paths are used so the
 * site never 404s on first paint. Subsequent calls across the app
 * share one in-memory cache. Call `invalidateCmsPageRoutes()` after
 * a publish that changes a slug to force a refresh.
 */
import { useEffect, useState } from "react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  home: "/",
  "shopify-dev": "/shopify-experts-development",
  marketing: "/ecommerce-marketing-agency",
  creative: "/creative",
  about: "/about",
};

const defaultsMap = () =>
  Object.fromEntries(
    Object.entries(DEFAULTS).map(([k, d]) => [k, { path: d, default: d }])
  );

let _cache = null;
let _promise = null;

const fetchRoutes = () => {
  if (_cache) return Promise.resolve(_cache);
  if (_promise) return _promise;
  _promise = fetch(`${BASE}/api/page-routes`, { credentials: "omit" })
    .then((r) => (r.ok ? r.json() : {}))
    .then((raw) => {
      const map = {};
      Object.entries(DEFAULTS).forEach(([k, d]) => {
        map[k] = {
          path: (raw && raw[k] && raw[k].path) || d,
          default: d,
        };
      });
      _cache = map;
      return map;
    })
    .catch(() => {
      _cache = defaultsMap();
      return _cache;
    });
  return _promise;
};

export function invalidateCmsPageRoutes() {
  _cache = null;
  _promise = null;
}

export function useCmsPageRoutes() {
  const [map, setMap] = useState(() => _cache || defaultsMap());
  useEffect(() => {
    let cancelled = false;
    fetchRoutes().then((m) => {
      if (!cancelled) setMap(m);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return map;
}
